/**
 * Zod schemas for User validation
 */

import { z } from 'zod';
import { nonEmptyOptionalString } from '@/types/schema';
import { ClaimsSchema } from '@/modules/idv';
import type { User } from './types';

export const UserSchema = z.object({
  id: z.string(),
  email: nonEmptyOptionalString,
  phoneTail: z.string(),
  phoneOprf: z.string(),
  regionCode: nonEmptyOptionalString,
  lastDeviceType: nonEmptyOptionalString,
  createdAt: z.string().transform(val => new Date(val)),
  updatedAt: z.string().transform(val => new Date(val)),
  claims: ClaimsSchema.default({}),
});

/**
 * Validate and parse User from unknown JSON.
 * Returns User or null if invalid.
 */
export function validateUser(data: unknown): User | null {
  const result = UserSchema.safeParse(data);
  if (!result.success) {
    return null;
  }
  return result.data;
}

/**
 * Parse User from API response. Expects { user: User | null }.
 * Returns null for guest (when data.user is null/undefined).
 */
export function validateUserInAPIResponse(data: unknown): User | null {
  if (data == null || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  const userData = obj.user;
  if (userData == null) return null;
  return validateUser(userData);
}
